import { CANVAS_AD_COLUMN_GAP, CANVAS_AD_ROW_GAP, CANVAS_EDIT_COLUMN_GAP } from '../../../../shared/canvasGeometry';
import type { CanvasPlaceholder, PlaybookQaResult } from './canvas-types';

export interface RecoverableRun {
  id: number;
  status?: string;
  skeletonKey?: string | null;
  input?: {
    message?: string;
    count?: number;
    canvasBounds?: {left?:number; top?:number; right?:number; bottom?:number};
    canvasPreview?: { count: number; aspectRatios?: string[]; x?: number; y?: number; slots?: Array<{id: string; x: number; y: number; width: number; height: number}> };
  };
}
type RecoveryImage = { id: string; src: string; x: number; y: number; displayWidth: number; displayHeight: number; workflowRunId?: number | null };
/** DB primary keys are numeric; planned slots use the persisted client ID. */
export function persistedCanvasImageId(row: { clientId?: string | null; client_id?: string | null }): string {
  return row.clientId || row.client_id || '';
}
export const isActiveRun = (run: RecoverableRun) => ['running', 'pending', 'in_progress'].includes(run.status ?? '');
const recovered = (p: CanvasPlaceholder) => p.taskId.startsWith('recovered-run-');

function legacyEditGrid(run:RecoverableRun,count:number) {
  if(run.skeletonKey!=='edit-fixes'||count<1)return;
  const {left,top,right,bottom}=run.input?.canvasBounds??{};
  if(typeof left!=='number'||typeof top!=='number'||typeof right!=='number'||typeof bottom!=='number')return;
  if(![left,top,right,bottom].every(Number.isFinite))return;
  const columns=Math.min(5,count);
  const rows=Math.ceil(count/5);
  const width=(right-left-(columns-1)*CANVAS_EDIT_COLUMN_GAP)/columns;
  const height=(bottom-top-(rows-1)*CANVAS_AD_ROW_GAP)/rows;
  if(width<=0||height<=0)return;
  return {left,top,width,height};
}

/** Rebuild only unresolved slots. Persisted image positions are never rewritten. */
export function recoverRunPlaceholders(runs: RecoverableRun[], images: RecoveryImage[], current: Record<string, CanvasPlaceholder>): Record<string, CanvasPlaceholder> {
  const next = Object.fromEntries(Object.entries(current).filter(([, p]) => {
    if (recovered(p)) return false;
    if (p.workflowRunId == null) return true;
    const run = runs.find(run=>run.id===p.workflowRunId);
    if (run && !isActiveRun(run)) return false;
    const slotId = p.slotIndex != null ? run?.input?.canvasPreview?.slots?.[p.slotIndex]?.id : undefined;
    const requestId = slotId ?? (p.taskId.startsWith('agent-img-') ? p.taskId.slice('agent-img-'.length) : undefined);
    return !images.some(image=>!!image.src && image.workflowRunId===p.workflowRunId &&
      (requestId ? image.id===requestId : Math.abs(image.x-p.x)<1 && Math.abs(image.y-p.y)<1));
  }));
  for (const run of [...runs].sort((a,b)=>a.id-b.id)) {
    if (!isActiveRun(run)) continue;
    const own = images.filter(image=>image.workflowRunId===run.id);
    const preview = run.input?.canvasPreview;
    // Without durable slot identities, retain the remaining live geometry.
    if (!preview?.slots?.length && Object.values(next).some(p=>p.workflowRunId===run.id)) continue;
    // Older runs did not persist the intent. Recover explicit numeric requests
    // only; never guess five missing outputs from a run with no known count.
    const legacyCount = /\b(\d{1,3})\s+(?:[\w-]+\s+){0,4}(?:ads?|creatives?|images?|variations?)\b/i.exec(run.input?.message ?? '');
    // A multi-source request repeats the full user message on each child run.
    // Its numeric count belongs to this workflow; the message can name the
    // entire batch ("edit all 20") and must not reserve 20 slots per source.
    const requestedCount = run.input?.count;
    const workflowCount = typeof requestedCount === 'number' && Number.isSafeInteger(requestedCount) && requestedCount >= 0
      ? requestedCount : undefined;
    const count = preview?.slots?.length || Math.min(100, Math.max(0, preview?.count ?? workflowCount ?? (legacyCount ? Number(legacyCount[1]) : own.length)));
    const editGrid=preview?.slots?.length?undefined:legacyEditGrid(run,count);
    const previous = images.filter(image=>image.workflowRunId !== run.id && (!image.workflowRunId || image.workflowRunId<run.id));
    const priorPending = Object.values(next).filter(p=>p.workflowRunId && p.workflowRunId<run.id);
    const left = preview?.x ?? editGrid?.left ?? (own.length ? Math.min(...own.map(i=>i.x)) : 60);
    const top = preview?.y ?? editGrid?.top ?? (own.length ? Math.min(...own.map(i=>i.y)) : Math.max(-60,...previous.map(i=>i.y+i.displayHeight),...priorPending.map(p=>p.y+p.height))+120);
    const ratios = preview?.aspectRatios?.length ? preview.aspectRatios : ['1:1'];
    const stepX=editGrid ? editGrid.width+CANVAS_EDIT_COLUMN_GAP : 300+CANVAS_AD_COLUMN_GAP;
    const stepY=(editGrid?.height??300)+CANVAS_AD_ROW_GAP;
    for (let index=0;index<count;index++) {
      const slot = preview?.slots?.[index];
      const plannedX=slot?.x ?? left+(index%5)*stepX, plannedY=slot?.y ?? top+Math.floor(index/5)*stepY;
      const atSlot = slot ? own.find(i=>i.id===slot.id) : own.find(i=>Math.abs(i.x-plannedX)<1&&Math.abs(i.y-plannedY)<1);
      const x=atSlot?.x ?? plannedX, y=atSlot?.y ?? plannedY;
      if (atSlot?.src) continue;
      const existing = Object.values(next).find(p=>p.workflowRunId===run.id &&
        (p.slotIndex===index || (slot && p.taskId===`agent-img-${slot.id}`) || (Math.abs(p.x-x)<1&&Math.abs(p.y-y)<1)));
      if (existing) {
        if (atSlot && (existing.x!==x || existing.y!==y)) next[existing.taskId] = { ...existing, x, y };
        continue;
      }
      const [rw,rh]=ratios[index%ratios.length].split(':').map(Number);
      const scale=300/Math.max(rw||1,rh||1);
      const taskId=`recovered-run-${run.id}-${index}`;
      next[taskId]={taskId,x,y,width:atSlot?.displayWidth||slot?.width||editGrid?.width||Math.round((rw||1)*scale),height:atSlot?.displayHeight||slot?.height||editGrid?.height||Math.round((rh||1)*scale),prompt:'Generating...',batchId:`run-${run.id}`,batchLabel:'Generating ads',workflowRunId:run.id,slotIndex:index};
    }
    // An explicit pending row is authoritative even if a legacy intent did not
    // record the count or the row has been moved outside the original grid.
    for (const row of own.filter(i=>!i.src)) {
      if (Object.values(next).some(p=>p.workflowRunId===run.id&&Math.abs(p.x-row.x)<1&&Math.abs(p.y-row.y)<1)) continue;
      const taskId=`recovered-run-${run.id}-image-${row.id}`;
      next[taskId]={taskId,x:row.x,y:row.y,width:row.displayWidth||300,height:row.displayHeight||300,prompt:'Generating...',batchId:`run-${run.id}`,workflowRunId:run.id};
    }
  }
  return next;
}

/** Restore final Canvas delivery by stable client identity, not provider attempt URL. */
export function recoveredRunImageSlots(message: import('./canvas-types').CanvasChatMessage, runId: number, images: readonly RecoveryImage[]) {
  const slots = [...(message.imageSlots ?? [])];
  const own = images.filter(image => image.workflowRunId === runId && !!image.src);
  const removed = new Set<number>();
  const belongs = (slot: import('./canvas-types').ChatImageSlot) => slot.workflowRunId == null || slot.workflowRunId === runId;
  for (const image of own) {
    const angle = /^angle-(\d+)-(\d+:\d+)-\d+-\d+$/.exec(image.id);
    const identity = angle ? {conceptKey:`${runId}:${angle[1]}`,aspectRatio:angle[2]} : undefined;
    const exact = slots.flatMap((slot,index) => !removed.has(index) && belongs(slot) && slot.requestId === image.id ? [index] : []);
    const concept = identity ? slots.flatMap((slot,index) => !removed.has(index) && belongs(slot) && slot.conceptKey === identity.conceptKey && slot.aspectRatio === identity.aspectRatio && !own.some(other=>other.id!==image.id&&other.id===slot.requestId) ? [index] : []) : [];
    // Legacy recovery appended URL-only slots. They may alias this one Canvas
    // client only if the URL identifies exactly one image in this workflow.
    const uniqueUrl = own.filter(other => other.src === image.src).length === 1;
    const aliases = uniqueUrl ? slots.flatMap((slot,index) => !removed.has(index) && belongs(slot) && !slot.requestId && !slot.conceptKey && slot.resolved === image.src ? [index] : []) : [];
    const pending = slots.findIndex((slot,index) => !removed.has(index) && belongs(slot) && !slot.resolved && !slot.failed && !slot.conceptKey && !slot.requestId);
    const target = exact[0] ?? (concept.length === 1 ? concept[0] : undefined) ?? aliases[0] ?? (pending >= 0 ? pending : undefined);
    const next = {requestId:image.id,workflowRunId:runId,...identity,resolved:image.src,failed:false,errorMsg:undefined};
    if (target !== undefined) {
      slots[target] = {...slots[target],...next};
      for (const index of [...exact,...aliases]) if (index !== target) removed.add(index);
    } else slots.push({index:Math.max(-1,...slots.map(slot=>slot.index))+1,...next});
  }
  return slots.filter((_,index)=>!removed.has(index));
}

/** Terminal edit QA describes the saved image, not its discarded attempts. */
export function recoveredEditQa(run: RecoverableRun, images: readonly (RecoveryImage & {qaPassed?: boolean | null})[]): PlaybookQaResult[] | undefined {
  if (run.skeletonKey !== 'edit-fixes' || isActiveRun(run)) return;
  const delivered = images.filter(image => image.workflowRunId === run.id && image.src);
  if (!delivered.length) return;
  return delivered.flatMap((image, conceptIndex) => typeof image.qaPassed === 'boolean' ? [{
    conceptIndex, attempt: 0, score: 0, passed: image.qaPassed,
    status: image.qaPassed ? 'passed' : 'needs_review',
  }] : []);
}
